<script>
  import cls from 'classnames';
  import {
    Button,
    DropdownDivider,
    NavBrand,
    NavHamburger,
    NavLi,
    NavUl,
    Input,
    Popover,
  } from 'flowbite-svelte';
  import {
    BullhornOutline,
    CloseOutline,
    PlusSolid,
    WandMagicSparklesOutline,
  } from 'flowbite-svelte-icons';
  import { _ } from 'svelte-i18n';
  import { Link } from 'svelte-routing';
  import { useLocation } from 'svelte-routing';
  import { queryUserData } from './data/userData';
  import TopNavLi from './TopNavLi.svelte';
  import LanguageSelector from './LanguageSelector.svelte';
  import AnnouncementBanner from './AnnouncementBanner.svelte';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import {
    ANDROID_APP_LAUNCH,
    CREATOR_CONNECTIONS_GENERAL_LAUNCH,
    CURATEDBY_LAUNCH_BANNER_2,
    GEOLOCATION_BETA_LAUNCH,
    QUERY_KEY_USER_DATA,
    MUTATION_KEY_PRETEND_MODE,
  } from './constants';
  import { client } from './lib/client';

  export let pages = [];
  export let showLanguageSelector = false;
  export let showCreateShortlink = false;

  let className;
  export { className as class };

  const userData = queryUserData();
  $: hasUserData = $userData.isSuccess;
  $: firstName = $userData.data?.object?.profile?.firstName;
  $: lastName = $userData.data?.object?.profile?.lastName;
  $: userProfileName =
    firstName || lastName ? `${firstName} ${lastName}`.trim() : '';
  $: userName =
    hasUserData && userProfileName.length > 0
      ? userProfileName
      : $userData.data?.object?.creatorName;
  const location = useLocation();
  const createShortlinkCopy = $_('TopNav.cta.createShortlink', {
    default: 'Create Shortlink',
  });

  let hidden = true;

  const toggleNav = () => {
    hidden = !hidden;
  };

  let pretendCreatorInputValue = '';

  function updatePretendCreatorInputValue(newValue) {
    pretendCreatorInputValue = newValue;
  }

  $: if ($userData.isSuccess) {
    updatePretendCreatorInputValue($userData.data?.object?.realCreatorId || '');
  }

  async function pretendCreator(pretendCreator) {
    if (!pretendCreator) {
      return client.delete('/api/pretend');
    }
    return client.post('/api/pretend', {
      creator_id: pretendCreator,
    });
  }

  const queryClient = useQueryClient();
  const pretendMutation = createMutation({
    mutationKey: MUTATION_KEY_PRETEND_MODE,
    mutationFn: pretendCreator,
    onSuccess: async () => {
      window.location.reload();
      await queryClient.invalidateQueries([QUERY_KEY_USER_DATA]);
    },
  });
</script>

<div class={className}>
  <!-- backdrop -->
  <div
    role="presentation"
    class={cls('fixed inset-0', {
      hidden,
    })}
    on:click={toggleNav}
  />
  <!-- top nav -->
  <div
    class="relative flex justify-between border-b border-gray-200 bg-white p-4"
  >
    <NavBrand href="/">
      <slot name="logo" />
    </NavBrand>
    <div class="hidden gap-4 divide-x md:flex">
      {#if showCreateShortlink}
        <Link to="/">
          <Button size="sm"
            ><PlusSolid class="me-2 h-3 w-3" />
            {createShortlinkCopy}
          </Button>
        </Link>
      {/if}
      <div class="flex flex-row items-center pl-4">
        <div class="text-right">
          {#if $userData.isSuccess}
            <div>{userName}</div>
            <div class="flex items-center justify-end gap-1">
              {#if $userData.data?.object?.pretendEnabled}
                <button id="pretend-mode-button">
                  {#if $userData.data?.object?.realCreatorId}
                    <div class="rounded-full bg-gray-700 p-1 text-yellow-200">
                      <WandMagicSparklesOutline class="h-4 w-4" />
                    </div>
                  {:else}
                    <div class="rounded-full bg-gray-500 p-1 text-white">
                      <WandMagicSparklesOutline class="h-4 w-4" />
                    </div>
                  {/if}
                </button>
                <Popover
                  triggeredBy="#pretend-mode-button"
                  trigger="click"
                  class="z-[99999]"
                >
                  <div class="flex flex-col gap-2">
                    <Input
                      placeholder="Creator ID"
                      bind:value={pretendCreatorInputValue}
                      disabled={$userData.data?.object?.realCreatorId}
                    />
                    <div class="flex gap-1">
                      {#if !$userData.data?.object?.realCreatorId}
                        <Button
                          class="w-full"
                          on:click={() =>
                            $pretendMutation.mutate(pretendCreatorInputValue)}
                          >Pretend</Button
                        >
                      {:else}
                        <Button
                          on:click={() => $pretendMutation.mutate(null)}
                          class="w-full"
                          color="alternative"
                        >
                          <CloseOutline />
                        </Button>
                      {/if}
                    </div>
                  </div>
                </Popover>
              {/if}
              <span class="text-xs text-gray-400"
                >{$userData.data?.object.creatorID}</span
              >
            </div>
          {/if}
        </div>
      </div>
    </div>
    <div class="md:hidden">
      <button class="flex flex-1 justify-end" on:click={toggleNav}>
        <NavHamburger />
      </button>
      <div class="absolute left-0 top-12 z-10 w-full px-4">
        <NavUl
          activeUrl={$location.pathname}
          {hidden}
          activeClass="flex py-4 text-blue-500"
          nonActiveClass="flex py-4 cursor-pointer text-gray-500"
          classUl="bg-white"
        >
          <NavLi class="cursor-default">
            <div role="presentation" on:click|stopPropagation class="text-left">
              {#if $userData.isSuccess}
                {userName}<br /><span class="text-xs text-gray-400"
                  >{$userData.data?.object.creatorID}</span
                >
              {/if}
            </div>
          </NavLi>
          {#if pages.length > 0}
            <DropdownDivider />
            {#each pages as { divider, meta, path, icon, onClick, badge }}
              {#if !divider}
                {#if path}
                  <Link to={path} on:click={onClick}>
                    <TopNavLi href={path} {icon} {badge}>{meta.title}</TopNavLi>
                  </Link>
                {:else}
                  <button on:click={onClick}>
                    <TopNavLi {icon} {badge}>{meta.title}</TopNavLi>
                  </button>
                {/if}
              {/if}
            {/each}
          {/if}
          {#if showLanguageSelector}
            <DropdownDivider class="!my-4" />
            <LanguageSelector />
            <DropdownDivider class="!my-4" />
          {/if}
          {#if showCreateShortlink}
            <DropdownDivider />
            <Link to="/">
              <Button class="mt-3 w-full" size="sm"
                ><PlusSolid class="me-2 h-3 w-3" />
                {createShortlinkCopy}</Button
              >
            </Link>
          {/if}
          <Button
            href="mailto:amazonsupport@usebutton.com"
            class="mt-2 bg-gray-200 text-black hover:bg-gray-300"
            ><BullhornOutline class="me-2 h-3 w-3" />
            {$_('TopNav.cta.submitFeedback', {
              default: 'Submit Feedback',
            })}
          </Button>
        </NavUl>
      </div>
    </div>
  </div>
  {#if $userData.data?.object?.ccEnabled}
    <AnnouncementBanner cookieKey={CREATOR_CONNECTIONS_GENERAL_LAUNCH}>
      🚀 We're excited to introduce Creator Connections in PostTap - available
      for US creators in the web dashboard. Click
      <a
        href="https://intercom.help/posttap/en/articles/11081242-creator-connections-is-now-supported-in-posttap"
        target="_blank"
        class="decoration-600 dark:decoration-500 inline font-medium text-primary-600 underline decoration-solid underline-offset-2 hover:no-underline dark:text-primary-500"
      >
        here
      </a>
      for more information.
    </AnnouncementBanner>
    <AnnouncementBanner cookieKey={ANDROID_APP_LAUNCH}>
      {$_('announcementBanner.androidLaunch.p1', {
        default: '🚀 In addition to the ',
      })}
      <a
        target="_blank"
        class="decoration-600 dark:decoration-500 inline font-medium text-primary-600 underline decoration-solid underline-offset-2 hover:no-underline dark:text-primary-500"
        href="https://apps.apple.com/us/app/posttap-by-button/id6504541857"
      >
        {$_('announcementBanner.androidLaunch.p2', {
          default: 'App Store,',
        })}
      </a>
      {$_('announcementBanner.androidLaunch.p3', {
        default: 'the PostTap App is now available on the Play Store - click ',
      })}
      <a
        target="_blank"
        class="decoration-600 dark:decoration-500 inline font-medium text-primary-600 underline decoration-solid underline-offset-2 hover:no-underline dark:text-primary-500"
        href="https://play.google.com/store/apps/details?id=com.usebutton.posttap"
        >{$_('announcementBanner.androidLaunch.p4', {
          default: 'here',
        })}
      </a>
      {$_('announcementBanner.androidLaunch.p5', {
        default: 'to check it out!',
      })}
    </AnnouncementBanner>
    <AnnouncementBanner cookieKey={CURATEDBY_LAUNCH_BANNER_2}>
      {$_('announcementBanner.curatedbyLaunch.part1', {
        default:
          'Introducing CuratedBy: Your trending and recent PostTap links automatically organized on one page for your followers—',
      })}
      <Link to="/curated-by" class="text-blue-500 underline"
        >{$_('announcementBanner.curatedbyLaunch.part2', {
          default: 'set it up',
        })}
      </Link>
      {$_('announcementBanner.curatedbyLaunch.part3', {
        default: 'in seconds.',
      })}
    </AnnouncementBanner>
    {#if $userData?.data?.object?.isGeolocationEnabled}
      <AnnouncementBanner cookieKey={GEOLOCATION_BETA_LAUNCH}>
        <span class="font-bold">
          🌎
          {$_('announcementBanner.geolocationBeta.part1', {
            default: 'New Feature Alert:',
          })}
        </span>
        {$_('announcementBanner.geolocationBeta.part2', {
          default: 'Unlock International Earnings — Get Started',
        })}
        <Link to="/geolocation" class="text-blue-500 underline"
          >{$_('announcementBanner.geolocationBeta.part3', {
            default: 'Here!',
          })}
        </Link>
      </AnnouncementBanner>
    {/if}
  {/if}
</div>
